import { ignoredProjectKeysConst, IgnoredProjectKeysTuple } from "@nova/constants"
import { Project } from "@nova/protobuf"
import { arrToMap, weakMemoize, omit } from "nodash"
import { sidePanelLayerTypeByProtobufEntityType, EntityType } from "./constants"
export { sidePanelLayerTypeByProtobufEntityType } from "./constants"
export type { EntityType } from "./constants"

type DataWithIdAndChildren = { id: string; parentId?: string; children?: DataWithIdAndChildren[] }
type DataWithIdAndOptionalParent = { id: string; parentId?: string }
export function getSiblings<T extends DataWithIdAndChildren>(
    id: string,
    dataFlatMap: Record<string, T | undefined>,
    sortedData: T[]
) {
    let curData = dataFlatMap[id]
    const path: string[] = []
    while (curData && curData.parentId && curData.parentId !== "null") {
        curData = dataFlatMap[curData.parentId]
        if (curData) path.push(curData.id)
    }
    let curSiblings: T[] = sortedData
    for (const pathStep of path.reverse()) {
        curSiblings = (curSiblings.find((data) => data.id === pathStep)?.children || []) as T[]
    }
    return curSiblings
}

export function getAncestors<T extends DataWithIdAndOptionalParent>(
    id: string,
    dataFlatMap: Record<string, T | undefined>
) {
    const ancestorIds: T[] = []
    let curData = dataFlatMap[id]
    while (curData && curData.parentId && curData.parentId !== "null") {
        curData = dataFlatMap[curData.parentId]
        if (curData) ancestorIds.push(curData)
    }
    return ancestorIds
}

export const getEntitiesFlat = weakMemoize((project: Project) => {
    const data = omit(project, ignoredProjectKeysConst) as Omit<Project, IgnoredProjectKeysTuple>

    const entities = Object.entries(data).flatMap(([entityType, entities]) => {
        if (entityType === "mergedFlights") {
            const infraredFlights = data.infraredFlights
            return entities.flatMap((mergedFlight) => {
                const flightsInMerge = infraredFlights.filter(
                    (flight) => flight.mergeId === mergedFlight.id
                )
                const flightIdsInMerge = flightsInMerge.map((flight) => flight.id)
                return {
                    ...mergedFlight,
                    hotspotFlightIds: flightIdsInMerge,
                    hotspotFlights: flightsInMerge,
                    type: sidePanelLayerTypeByProtobufEntityType[entityType as EntityType],
                }
            })
        } else
            return entities.flatMap((entity) => ({
                ...entity,
                type: sidePanelLayerTypeByProtobufEntityType[entityType as EntityType],
            }))
    })

    return entities
})

export const getEntitiesMap = weakMemoize((project: Project) =>
    arrToMap(getEntitiesFlat(project), "id")
)

// e.visible === undefined means visible, legacy thing that shouldnt be relevant
export const entityIsVisible =
    (project: Project) => (e: { id: string; visible: boolean; archivedTimestamp?: number }) =>
        e.visible !== false &&
        !e.archivedTimestamp &&
        !getAncestors(e.id, getEntitiesMap(project)).find(
            (e) => "visible" in e && e.visible === false
        )
